/*
 * This file is part of BlueMap, licensed under the MIT License (MIT).
 *
 * Copyright (c) Blue (Lukas Rieger) <https://bluecolored.de>
 * Copyright (c) contributors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
import {Marker} from "./markers/Marker";
import {CSS2DObject} from "./util/CSS2DRenderer";
import {animate, htmlToElement} from "./util/Utils";
import {BoxGeometry, MeshBasicMaterial, Mesh, Vector2} from "three";
import {i18n} from "../i18n";

export class PopupMarker extends Marker {

    constructor(id, appState, events) {
        super(id);

        this.data.type = "popup";
        this.data.label = "Last Map Interaction";
        this.data.listed = false;

        this.appState = appState;
        this.events = events;
        this.visible = false;

        this.elementObject = new CSS2DObject(htmlToElement(`<div id="bm-marker-${this.data.id}" class="bm-marker-${this.data.type}">Test</div>`));
        this.elementObject.position.set(0.5, 1, 0.5);
        this.elementObject.disableDepthTest = true;
        this.addEventListener( 'removed', () => {
            if (this.element.parentNode) this.element.parentNode.removeChild(this.element);
        });

        let cubeGeo = new BoxGeometry(1.01, 1.01, 1.01).translate(0.5, 0.5, 0.5);
        let cubeMaterial = new MeshBasicMaterial( {color: 0xffffff, opacity: 0.5, transparent: true} );
        this.cube = new Mesh(cubeGeo, cubeMaterial);
        this.cube.onClick = evt => this.onClick(evt);

        this.add(this.elementObject);
        this.add(this.cube);

        this.animation = null;

        this.events.addEventListener('bluemapMapInteraction', evt => window.setTimeout(() => this.onMapInteraction(evt)));

        window.addEventListener("mousedown", this.removeHandler);
        window.addEventListener("touchstart", this.removeHandler, { passive: true });
        window.addEventListener("keydown", this.removeHandler);
        window.addEventListener("mousewheel", this.removeHandler);
    }

    onClick(event) {
        return true;
    }

    static copyToClipboard = (text) => {
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(text)
                .catch(e => console.error(e));
        } else {
            function listener(e) {
                e.clipboardData.setData("text/plain", text);
                e.preventDefault();
            }
            document.addEventListener("copy", listener);
            document.execCommand("copy");
            document.removeEventListener("copy", listener);
        }
    }
    static blockClipboardFormat  = (pos, isHires) => isHires ? `${pos.x} ${pos.y} ${pos.z}` : `${pos.x} ${pos.z}`;
    static chunkClipboardFormat  = (pos, isHires) => isHires ? `${pos.x} ${pos.y} ${pos.z}` : `${pos.x} ${pos.z}`;
    static regionClipboardFormat = pos => `r.${pos.x}.${pos.y}.mca`
    static escapeText = value => String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;")
        .replaceAll("'", "&#039;");

    static toWgs84 = (x, z) => {
        const y = -z;
        const dX = (x - 155000) * Math.pow(10, -5);
        const dY = (y - 463000) * Math.pow(10, -5);
        const somN =
            3235.65389 * dY +
            (-32.58297 * Math.pow(dX, 2) +
                -0.2475 * Math.pow(dY, 2) +
                -0.84978 * Math.pow(dX, 2) * dY +
                -0.0655 * Math.pow(dY, 3) +
                -0.01709 * Math.pow(dX, 2) * Math.pow(dY, 2) +
                -0.00738 * dX +
                0.0053 * Math.pow(dX, 4) +
                -0.00039 * Math.pow(dX, 2) * Math.pow(dY, 3) +
                0.00033 * Math.pow(dX, 4) * dY +
                -0.00012 * dX * dY);
        const somE =
            5260.52916 * dX +
            105.94684 * dX * dY +
            2.45656 * dX * Math.pow(dY, 2) +
            -0.81885 * Math.pow(dX, 3) +
            0.05594 * dX * Math.pow(dY, 3) +
            -0.05607 * Math.pow(dX, 3) * dY +
            0.01199 * dY +
            -0.00256 * Math.pow(dX, 3) * Math.pow(dY, 2) +
            0.00128 * dX * Math.pow(dY, 4) +
            0.00022 * Math.pow(dY, 2) +
            -0.00022 * Math.pow(dX, 2) +
            0.00026 * Math.pow(dX, 5);

        return {
            latitude: 52.15517 + somN / 3600,
            longitude: 5.387206 + somE / 3600
        };
    };

    static reverseGeoLookup = async (x, z) => {
        try {
            const { latitude, longitude } = PopupMarker.toWgs84(x, z);
            const response = await fetch(`https://geocraft.nl/geoservice/?key=${import.meta.env.VITE_GEOSERVICE}&action=reverse&lat=${latitude}&long=${longitude}`);
            const data = await response.json();
            if (!Array.isArray(data.results) || data.results.length === 0) return null;
            return data.results[0];
        } catch (e) {
            console.error("Reverse geocoding failed:", e);
            return null;
        }
    };

    onMapInteraction = async evt => {
        let isHires = true;
        let int = evt.detail.hiresHit;

        if (evt.detail.lowresHits) {
            for (let i = 0; i < evt.detail.lowresHits.length; i++) {
                if (!int) {
                    isHires = false;
                    int = evt.detail.lowresHits[i];
                } else break;
            }
        }

        if (!int) return;

        this.position
            .copy(int.pointOnLine || int.point)
            .add(evt.detail.ray.direction.clone().multiplyScalar(0.05))
            .floor();

        const location = !isHires ? await PopupMarker.reverseGeoLookup(this.position.x, this.position.z) : null;

        //this.elementObject.position
            //.copy(evt.detail.intersection.pointOnLine || evt.detail.intersection.point)
            //.sub(this.position);

        if (isHires) {
            this.element.innerHTML = `
                <div class="group" 
                     data-tooltip="${i18n.t("blockTooltip.clipboard")}"
                     onclick="BlueMap.PopupMarker.copyToClipboard('${PopupMarker.blockClipboardFormat(this.position, true)}')" >
                    <div class="label">${i18n.t("blockTooltip.block")}:</div>
                    <div class="content">
                        <div class="entry"><span class="label">x: </span><span class="value">${this.position.x}</span></div>
                        <div class="entry"><span class="label">y: </span><span class="value">${this.position.y}</span></div>
                        <div class="entry"><span class="label">z: </span><span class="value">${this.position.z}</span></div>
                    </div>
                </div>
            `;
        } else {
            const locationHtml = location ? `
                    <div class="label">${i18n.t("blockTooltip.location.title")}:</div>
                    <div class="content">
                        <div class="entry"><span class="label">${i18n.t("blockTooltip.location.town")}: </span><span class="block">${PopupMarker.escapeText(location.city)}</span></div>
                        <div class="entry"><span class="label">${i18n.t("blockTooltip.location.zip")}: </span><span class="block">${PopupMarker.escapeText(location.zip)}</span></div>
                        <div class="entry"><span class="label">${i18n.t("blockTooltip.location.street")}: </span><span class="block">${PopupMarker.escapeText(location.street)} ${PopupMarker.escapeText(location.streetnumber)}</span></div>
                    </div>
            ` : "";
            this.element.innerHTML = `
                <div class="group"
                     data-tooltip="${i18n.t("blockTooltip.clipboard")}"
                     onclick="BlueMap.PopupMarker.copyToClipboard('${PopupMarker.blockClipboardFormat(this.position, false)}')" >
                    <div class="label">${i18n.t("blockTooltip.position")}:</div>
                    <div class="content">
                        <div class="entry"><span class="label">x: </span><span class="value">${this.position.x}</span></div>
                        <div class="entry"><span class="label">z: </span><span class="value">${this.position.z}</span></div>
                    </div>
                    ${locationHtml}
                </div>
            `;
        }

        if (this.appState.debug) {
            let chunkCoords = this.position.clone().divideScalar(16).floor();
            let regionCoords = new Vector2(this.position.x, this.position.z).divideScalar(512).floor();
            let regionFile = `r.${regionCoords.x}.${regionCoords.y}.mca`;

            if (isHires) {
                this.element.innerHTML += `
                    <hr>
                    <div class="group"
                         data-tooltip="${i18n.t("blockTooltip.clipboard")}"
                         onclick="BlueMap.PopupMarker.copyToClipboard('${PopupMarker.chunkClipboardFormat(chunkCoords, true)}')" >
                        <div class="label">${i18n.t("blockTooltip.chunk")}:</div>
                        <div class="content">
                            <div class="entry"><span class="label">x: </span><span class="value">${chunkCoords.x}</span></div>
                            <div class="entry"><span class="label">y: </span><span class="value">${chunkCoords.y}</span></div>
                            <div class="entry"><span class="label">z: </span><span class="value">${chunkCoords.z}</span></div>
                        </div>
                    </div>
                `;
            } else {
                this.element.innerHTML += `
                    <hr>
                    <div class="group"
                         data-tooltip="${i18n.t("blockTooltip.clipboard")}"
                         onclick="BlueMap.PopupMarker.copyToClipboard('${PopupMarker.chunkClipboardFormat(chunkCoords, false)}')" >
                        <div class="label">${i18n.t("blockTooltip.chunk")}:</div>
                        <div class="content">
                            <div class="entry"><span class="label">x: </span><span class="value">${chunkCoords.x}</span></div>
                            <div class="entry"><span class="label">z: </span><span class="value">${chunkCoords.z}</span></div>
                        </div>
                    </div>
                `;
            }

            this.element.innerHTML += `
                <hr>
                <div class="group"
                     data-tooltip="${i18n.t("blockTooltip.clipboard")}"
                     onclick="BlueMap.PopupMarker.copyToClipboard('${PopupMarker.regionClipboardFormat(regionCoords)}')" >
                    <div class="label">${i18n.t("blockTooltip.region.region")}:</div>
                    <div class="content">
                        <div class="entry"><span class="label">x: </span><span class="value">${regionCoords.x}</span></div>
                        <div class="entry"><span class="label">z: </span><span class="value">${regionCoords.y}</span></div>
                    </div>
                    <div class="content">
                        <div class="entry"><span class="label">${i18n.t("blockTooltip.region.file")}: </span><span class="value">${regionFile}</span></div>
                    </div>
                </div>
            `;
        }

        if (this.appState.debug) {
            let faceIndex = int.faceIndex;
            let attributes = int.object.geometry.attributes;
            if (attributes.sunlight && attributes.blocklight) {
                let sunlight = attributes.sunlight.array[faceIndex * 3];
                let blocklight = attributes.blocklight.array[faceIndex * 3];

                this.element.innerHTML += `
                    <hr>
                    <div class="group">
                        <div class="label">${i18n.t("blockTooltip.light.light")}:</div>
                        <div class="content">
                            <div class="entry"><span class="label">${i18n.t("blockTooltip.light.sun")}: </span><span class="value">${sunlight}</span></div>
                            <div class="entry"><span class="label">${i18n.t("blockTooltip.light.block")}: </span><span class="value">${blocklight}</span></div>
                        </div>
                    </div>
                `;
            }
        }

        if (this.appState.debug) {
            let info = "";

            if (isHires) {
                let hrPath = evt.detail.hiresHit?.object?.userData?.tileUrl;
                info += `<div>${hrPath}</div>`;
            }

            if (evt.detail.lowresHits) {
                for (let i = 0; i < evt.detail.lowresHits.length; i++) {
                    let lrPath = evt.detail.lowresHits[i]?.object?.userData?.tileUrl;
                    if (lrPath) info += `<div>${lrPath}</div>`;
                }
            }

            this.element.innerHTML += `
                <hr>
                <div class="files">
                    ${info}
                </div>
            `;
        }

        if (this.appState.debug){
            console.debug("Clicked Position Data:", evt.detail);
        }

        this.open();
    };

    open() {
        if (this.animation) this.animation.cancel();

        this.visible = true;
        this.cube.visible = true;

        let targetOpacity = 1;

        this.element.style.opacity = "0";
        this.animation = animate(progress => {
            this.element.style.opacity = (progress * targetOpacity).toString();
        }, 300);
    }

    removeHandler = evt => {
        if (evt.composedPath().includes(this.element)) return;
        this.close();
    }

    close() {
        if (this.animation) this.animation.cancel();

        this.cube.visible = false;

        let startOpacity = parseFloat(this.element.style.opacity);
        this.animation = animate(progress => {
            this.element.style.opacity = (startOpacity - progress * startOpacity).toString();
        }, 300, finished => {
            if (finished) this.visible = false;
        });
    }

    /**
     * @returns {Element}
     */
    get element() {
        return this.elementObject.element.getElementsByTagName("div")[0];
    }

    dispose() {
        super.dispose();

        if (this.element.parentNode) this.element.parentNode.removeChild(this.element);
    }

}
